import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    ArrowDown12Regular,
    Checkmark12Filled,
    Dismiss16Regular,
    Search16Regular,
    SpinnerIos16Regular,
    Warning16Regular,
} from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import { CONTEXT_MODE } from '../../hooks/useLensIntelligenceScope';
import { fetchIntelligenceScopeCatalog } from '../../redux/intelligenceSlice';
import {
    SCOPE_SENTINEL,
    workstreamOptionsFromList,
} from '../../utils/aiWardenScope';
import {
    cloneCustomContext,
    createEmptyCustomContext,
    formatIrlRowLabel,
    formatObservationRowLabel,
    formatVdrDocumentLabel,
    materializeCustomContextWorkstreams,
    validateCustomContext,
} from '../../utils/lensIntelligenceCustomContext';
import {
    countIntelligenceSourceReadiness,
    isIntelligenceSourceProcessing,
    projectLevelOriginLabel,
} from '../../utils/intelligenceSourceReadiness';
import { activeIndexingFileIds, getActiveIndexingFiles } from '../../utils/vdrIndexingQueue';
import styles from './askAi.module.css';

const TABS = [
    { key: CONTEXT_MODE.ENTIRE_PROJECT, label: 'Entire Project' },
    { key: CONTEXT_MODE.SELECT_WORKSTREAMS, label: 'Workstreams' },
    { key: CONTEXT_MODE.CUSTOM, label: 'Custom Sources' },
];

const TYPE_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'vdr', label: 'VDR' },
    { key: 'irl', label: 'IRL' },
    { key: 'obs', label: 'Observations' },
];

const STATUS_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'ready', label: 'Ready' },
    { key: 'processing', label: 'Processing' },
];

function isProcessingItem(item) {
    return isIntelligenceSourceProcessing(item);
}

function formatBytes(n) {
    const num = Number(n);
    if (!Number.isFinite(num) || num <= 0) return null;
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    return `${(num / (1024 * 1024)).toFixed(1)} MB`;
}

function formatShortDate(value) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function ReadyDot() {
    return <span className={styles.chooseReadyDot} aria-hidden />;
}

function ProcessingIcon({ className }) {
    return (
        <SpinnerIos16Regular
            className={`${styles.chooseProcessingIcon} ${className || ''}`}
            aria-hidden
        />
    );
}

function buildCustomContextFromSelection(selectedKeys, catalog, workstreams) {
    const next = createEmptyCustomContext();
    const wsOptions = workstreamOptionsFromList(workstreams);
    const wsById = new Map(wsOptions.map(o => [String(o.value), o]));

    const vdrIds = [];
    const irlIds = [];
    const obsIds = [];
    const vdrWs = new Set();
    const irlWs = new Set();
    const obsWs = new Set();

    for (const key of selectedKeys) {
        const row = catalog.find(r => r.key === key);
        if (!row) continue;
        if (row.type === 'vdr') {
            vdrIds.push(String(row.id));
            if (row.workstreamId) vdrWs.add(String(row.workstreamId));
        } else if (row.type === 'irl') {
            irlIds.push(String(row.id));
            if (row.workstreamId) irlWs.add(String(row.workstreamId));
        } else if (row.type === 'obs') {
            obsIds.push(String(row.id));
            if (row.workstreamId) obsWs.add(String(row.workstreamId));
        }
    }

    const pickWs = ids => {
        const opts = [...ids].map(id => wsById.get(id)).filter(Boolean);
        if (opts.length) return opts;
        // Fallback when rows lack workstream_id (e.g. some observations)
        return wsOptions;
    };

    next.documents.enabled = vdrIds.length > 0;
    next.documents.workstreams = pickWs(vdrWs);
    next.documents.selectedFileIds =
        vdrIds.length > 0 ? vdrIds : [SCOPE_SENTINEL.NONE];

    next.irl.enabled = irlIds.length > 0;
    next.irl.workstreams = pickWs(irlWs);
    next.irl.selectedIrlIds = irlIds.length > 0 ? irlIds : [SCOPE_SENTINEL.NONE];

    next.observations.enabled = obsIds.length > 0;
    next.observations.workstreams = pickWs(obsWs);
    next.observations.selectedObservationIds =
        obsIds.length > 0 ? obsIds : [SCOPE_SENTINEL.NONE];

    return materializeCustomContextWorkstreams(next, workstreams);
}

function seedSelectedKeysFromCustom(config, catalog) {
    if (!config) return new Set();
    const keys = new Set();
    const fileIds = new Set((config.documents?.selectedFileIds || []).map(String));
    const irlIds = new Set((config.irl?.selectedIrlIds || []).map(String));
    const obsIds = new Set((config.observations?.selectedObservationIds || []).map(String));

    const filesExplicit =
        fileIds.size > 0 && !fileIds.has(SCOPE_SENTINEL.NONE) && config.documents?.enabled;
    const irlExplicit =
        irlIds.size > 0 && !irlIds.has(SCOPE_SENTINEL.NONE) && config.irl?.enabled;
    const obsExplicit =
        obsIds.size > 0 && !obsIds.has(SCOPE_SENTINEL.NONE) && config.observations?.enabled;

    for (const row of catalog) {
        if (row.type === 'vdr' && filesExplicit && fileIds.has(String(row.id))) {
            keys.add(row.key);
        }
        if (row.type === 'irl' && irlExplicit && irlIds.has(String(row.id))) {
            keys.add(row.key);
        }
        if (row.type === 'obs' && obsExplicit && obsIds.has(String(row.id))) {
            keys.add(row.key);
        }
    }
    return keys;
}

const AskAiChooseContextModal = ({
    open,
    onClose,
    onApply,
    initialMode = CONTEXT_MODE.ENTIRE_PROJECT,
    initialWorkstreams = [],
    initialCustomContext = null,
    workstreams = [],
    projectId,
    scopeStats = null,
    indexingFiles = [],
}) => {
    const dispatch = useDispatch();
    const {
        scopeCatalogProjectId,
        scopeCatalog,
        scopeCatalogLoading,
    } = useSelector(state => state.intelligence) || {};

    const catalogMatchesProject =
        projectId && String(scopeCatalogProjectId) === String(projectId);
    const docs = catalogMatchesProject ? scopeCatalog?.documents || [] : [];
    const irls = catalogMatchesProject ? scopeCatalog?.irls || [] : [];
    const observations = catalogMatchesProject ? scopeCatalog?.observations || [] : [];
    const catalogLoading = Boolean(scopeCatalogLoading);

    const indexingFileIds = useMemo(
        () => activeIndexingFileIds(indexingFiles, { projectId }),
        [indexingFiles, projectId],
    );

    const indexingById = useMemo(() => {
        const map = new Map();
        for (const item of getActiveIndexingFiles(indexingFiles, { projectId })) {
            const id = Number(item?.id);
            if (!Number.isFinite(id)) continue;
            map.set(id, item);
        }
        return map;
    }, [indexingFiles, projectId]);

    const [tab, setTab] = useState(initialMode);
    const [draftWorkstreams, setDraftWorkstreams] = useState([]);
    const [wsSearch, setWsSearch] = useState('');
    const [sourceSearch, setSourceSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [wsFilter, setWsFilter] = useState('all');
    const [selectedSourceKeys, setSelectedSourceKeys] = useState(() => new Set());
    const [showProcessingDetails, setShowProcessingDetails] = useState(false);

    const wsNameById = useMemo(() => {
        const map = new Map();
        for (const ws of workstreams || []) {
            map.set(String(ws.id), ws.name || `Workstream ${ws.id}`);
        }
        return map;
    }, [workstreams]);

    useEffect(() => {
        if (!open) return;
        setTab(initialMode || CONTEXT_MODE.ENTIRE_PROJECT);
        setDraftWorkstreams(
            (initialWorkstreams || []).map(o => ({ ...o, value: String(o.value) })),
        );
        setWsSearch('');
        setSourceSearch('');
        setTypeFilter('all');
        setStatusFilter('all');
        setWsFilter('all');
        setShowProcessingDetails(false);
    }, [open, initialMode, initialWorkstreams]);

    useEffect(() => {
        if (!open || !projectId) return;
        dispatch(fetchIntelligenceScopeCatalog({ projectId }));
    }, [open, projectId, dispatch]);

    const sourceCatalog = useMemo(() => {
        const rows = [];
        const resolveWsId = (wsId, wsName) => {
            if (wsId != null && wsId !== '') return String(wsId);
            const name = String(wsName || '').trim().toLowerCase();
            if (!name) return null;
            for (const [id, label] of wsNameById.entries()) {
                if (String(label || '').trim().toLowerCase() === name) return id;
            }
            return null;
        };

        for (const doc of docs) {
            const id = Number(doc?.id);
            const processing = Number.isFinite(id) && indexingById.has(id);
            const originLabel = projectLevelOriginLabel(doc.content_origin);
            const originKey = originLabel
                ? `origin:${
                      doc.content_origin === 'project_mail_attachment'
                          ? 'project_mail'
                          : doc.content_origin
                  }`
                : null;
            const workstreamId = originKey || resolveWsId(doc.workstream_id, doc.workstream_name);
            rows.push({
                key: `vdr-${doc.id}`,
                type: 'vdr',
                id: doc.id,
                title: formatVdrDocumentLabel(doc),
                workstreamId,
                workstreamName:
                    originLabel ||
                    (workstreamId && wsNameById.get(workstreamId)) ||
                    doc.workstream_name ||
                    'VDR',
                sizeLabel: formatBytes(doc.file_size || doc.size),
                dateLabel: formatShortDate(
                    doc.updated_at || doc.created_at || doc.modified_at,
                ),
                status: processing ? 'processing' : 'ready',
                raw: doc,
            });
        }
        for (const row of irls) {
            const processing = isProcessingItem(row);
            const workstreamId = resolveWsId(row.workstream_id, row.workstream_name);
            rows.push({
                key: `irl-${row.id}`,
                type: 'irl',
                id: row.id,
                title: formatIrlRowLabel(row),
                workstreamId,
                workstreamName:
                    (workstreamId && wsNameById.get(workstreamId)) ||
                    row.workstream_name ||
                    'IRL',
                sizeLabel: null,
                dateLabel: formatShortDate(row.updated_at || row.created_at),
                status: processing ? 'processing' : 'ready',
                raw: row,
            });
        }
        for (const row of observations) {
            const processing = isProcessingItem(row);
            const workstreamId = resolveWsId(row.workstream_id, row.workstream_repr);
            rows.push({
                key: `obs-${row.id}`,
                type: 'obs',
                id: row.id,
                title: formatObservationRowLabel(row),
                workstreamId,
                workstreamName:
                    (workstreamId && wsNameById.get(workstreamId)) ||
                    row.workstream_repr ||
                    'Observation',
                sizeLabel: null,
                dateLabel: formatShortDate(row.updated_at || row.created_at),
                status: processing ? 'processing' : 'ready',
                raw: row,
            });
        }

        // In-flight VDR uploads not yet in the lightweight catalog (or not marked processing).
        const seenVdrIds = new Set(
            rows.filter(r => r.type === 'vdr').map(r => Number(r.id)).filter(Number.isFinite),
        );
        for (const [id, item] of indexingById.entries()) {
            const existing = rows.find(r => r.type === 'vdr' && Number(r.id) === id);
            if (existing) {
                if (existing.status !== 'processing') {
                    existing.status = 'processing';
                }
                continue;
            }
            if (seenVdrIds.has(id)) continue;
            const workstreamId =
                item?.wsId != null && item.wsId !== '' ? String(item.wsId) : null;
            rows.push({
                key: `vdr-indexing-${id}`,
                type: 'vdr',
                id,
                title: `Document ${id}`,
                workstreamId,
                workstreamName:
                    (workstreamId && wsNameById.get(workstreamId)) || 'VDR',
                sizeLabel: null,
                dateLabel: null,
                status: 'processing',
                raw: { id, indexing_status: 'processing', workstream_id: item?.wsId },
            });
            seenVdrIds.add(id);
        }
        return rows;
    }, [docs, irls, observations, wsNameById, indexingById]);

    useEffect(() => {
        if (!open || !sourceCatalog.length) return;
        if (initialMode === CONTEXT_MODE.CUSTOM) {
            setSelectedSourceKeys(seedSelectedKeysFromCustom(initialCustomContext, sourceCatalog));
        }
    }, [open, sourceCatalog, initialMode, initialCustomContext]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = e => {
            if (e.key === 'Escape') onClose?.();
        };
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    const readiness = useMemo(() => {
        const counted = countIntelligenceSourceReadiness({
            documents: docs,
            irls,
            observations,
            indexingFileIds,
        });
        return {
            ...counted,
            readyTotal:
                scopeStats && !catalogLoading
                    ? counted.readyTotal ||
                      (Number(scopeStats.documents) || 0) +
                          (Number(scopeStats.irlResponses) || 0) +
                          (Number(scopeStats.observations) || 0)
                    : counted.readyTotal,
        };
    }, [docs, irls, observations, indexingFileIds, scopeStats, catalogLoading]);

    const workstreamRows = useMemo(() => {
        const map = new Map();
        const nameToId = new Map();
        for (const ws of workstreams || []) {
            const id = String(ws.id);
            const name = ws.name || `Workstream ${ws.id}`;
            map.set(id, {
                id,
                name,
                vdr: 0,
                irl: 0,
                obs: 0,
                ready: 0,
                processing: 0,
            });
            nameToId.set(String(name).trim().toLowerCase(), id);
        }
        const resolveWsId = (wsId, wsName) => {
            if (wsId != null && wsId !== '' && map.has(String(wsId))) return String(wsId);
            const key = String(wsName || '').trim().toLowerCase();
            return key ? nameToId.get(key) || null : null;
        };
        const bump = (wsId, field, processing) => {
            const id = String(wsId ?? '');
            if (!id || !map.has(id)) return;
            const row = map.get(id);
            row[field] += 1;
            if (processing) row.processing += 1;
            else row.ready += 1;
        };
        const catalogDocIds = new Set();
        for (const doc of docs) {
            const id = Number(doc?.id);
            if (Number.isFinite(id)) catalogDocIds.add(id);
            const processing = Number.isFinite(id) && indexingById.has(id);
            bump(
                resolveWsId(doc.workstream_id, doc.workstream_name),
                'vdr',
                processing,
            );
        }
        for (const row of irls) {
            bump(
                resolveWsId(row.workstream_id, row.workstream_name),
                'irl',
                false,
            );
        }
        for (const row of observations) {
            bump(
                resolveWsId(row.workstream_id, row.workstream_repr),
                'obs',
                false,
            );
        }
        for (const [id, item] of indexingById.entries()) {
            if (catalogDocIds.has(id)) continue;
            bump(item?.wsId, 'vdr', true);
        }
        return [...map.values()];
    }, [workstreams, docs, irls, observations, indexingById]);

    const filteredWorkstreams = useMemo(() => {
        const q = wsSearch.trim().toLowerCase();
        if (!q) return workstreamRows;
        return workstreamRows.filter(row => row.name.toLowerCase().includes(q));
    }, [workstreamRows, wsSearch]);

    const selectedWsSet = useMemo(
        () => new Set((draftWorkstreams || []).map(o => String(o.value))),
        [draftWorkstreams],
    );

    const originFilterOptions = useMemo(() => {
        const seen = new Map();
        for (const row of sourceCatalog) {
            if (row.type !== 'vdr' || !String(row.workstreamId || '').startsWith('origin:')) continue;
            if (!seen.has(row.workstreamId)) {
                seen.set(row.workstreamId, row.workstreamName);
            }
        }
        return [...seen.entries()].map(([value, label]) => ({ value, label }));
    }, [sourceCatalog]);

    const filteredSources = useMemo(() => {
        const q = sourceSearch.trim().toLowerCase();
        return sourceCatalog.filter(row => {
            if (typeFilter !== 'all' && row.type !== typeFilter) return false;
            if (statusFilter !== 'all' && row.status !== statusFilter) return false;
            if (wsFilter !== 'all' && row.workstreamId !== wsFilter) return false;
            if (q) {
                const hay = `${row.title} ${row.workstreamName}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [sourceCatalog, sourceSearch, typeFilter, statusFilter, wsFilter]);

    const readyFilteredSources = useMemo(
        () => filteredSources.filter(r => r.status === 'ready'),
        [filteredSources],
    );

    const allReadyResultsSelected = useMemo(
        () =>
            readyFilteredSources.length > 0 &&
            readyFilteredSources.every(row => selectedSourceKeys.has(row.key)),
        [readyFilteredSources, selectedSourceKeys],
    );

    const processingDocs = useMemo(() => {
        // Match the badge: only in-flight VDR indexing queue entries.
        const indexingIds = new Set(indexingFileIds);
        return sourceCatalog.filter(
            r => r.type === 'vdr' && r.status === 'processing' && indexingIds.has(Number(r.id)),
        );
    }, [sourceCatalog, indexingFileIds]);

    const toggleWorkstream = useCallback(
        wsId => {
            const id = String(wsId);
            setDraftWorkstreams(prev => {
                const exists = prev.some(o => String(o.value) === id);
                if (exists) return prev.filter(o => String(o.value) !== id);
                const opt = workstreamOptionsFromList(workstreams).find(
                    o => String(o.value) === id,
                );
                return opt ? [...prev, opt] : prev;
            });
        },
        [workstreams],
    );

    const toggleSource = useCallback(key => {
        setSelectedSourceKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const selectAllReadyResults = useCallback(() => {
        setSelectedSourceKeys(prev => {
            const next = new Set(prev);
            for (const row of readyFilteredSources) next.add(row.key);
            return next;
        });
    }, [readyFilteredSources]);

    const deselectAllReadyResults = useCallback(() => {
        setSelectedSourceKeys(prev => {
            const next = new Set(prev);
            for (const row of readyFilteredSources) next.delete(row.key);
            return next;
        });
    }, [readyFilteredSources]);

    const footerSummary = useMemo(() => {
        if (tab === CONTEXT_MODE.ENTIRE_PROJECT) {
            return `Entire Project selected · ${readiness.readyTotal} ready sources`;
        }
        if (tab === CONTEXT_MODE.SELECT_WORKSTREAMS) {
            const n = draftWorkstreams.length;
            if (!n) return 'No workstreams selected';
            return `${n} workstream${n === 1 ? '' : 's'} selected`;
        }
        const n = selectedSourceKeys.size;
        if (!n) return 'No sources selected';
        return `${n} source${n === 1 ? '' : 's'} selected`;
    }, [tab, readiness.readyTotal, draftWorkstreams.length, selectedSourceKeys.size]);

    const canApply = useMemo(() => {
        if (tab === CONTEXT_MODE.ENTIRE_PROJECT) return true;
        if (tab === CONTEXT_MODE.SELECT_WORKSTREAMS) return draftWorkstreams.length > 0;
        return selectedSourceKeys.size > 0;
    }, [tab, draftWorkstreams.length, selectedSourceKeys.size]);

    const handleApply = () => {
        if (tab === CONTEXT_MODE.ENTIRE_PROJECT) {
            onApply?.({
                mode: CONTEXT_MODE.ENTIRE_PROJECT,
                selectedWorkstreams: workstreamOptionsFromList(workstreams),
                customContext: createEmptyCustomContext(),
            });
            return;
        }
        if (tab === CONTEXT_MODE.SELECT_WORKSTREAMS) {
            if (!draftWorkstreams.length) {
                toast.error('Select at least one workstream.');
                return;
            }
            onApply?.({
                mode: CONTEXT_MODE.SELECT_WORKSTREAMS,
                selectedWorkstreams: draftWorkstreams,
                customContext: createEmptyCustomContext(),
            });
            return;
        }
        const config = buildCustomContextFromSelection(
            selectedSourceKeys,
            sourceCatalog,
            workstreams,
        );
        const errors = validateCustomContext(config, (workstreams || []).map(w => String(w.id)));
        if (errors.length) {
            toast.error(errors[0]);
            return;
        }
        onApply?.({
            mode: CONTEXT_MODE.CUSTOM,
            selectedWorkstreams: draftWorkstreams.length
                ? draftWorkstreams
                : workstreamOptionsFromList(workstreams),
            customContext: cloneCustomContext(config),
        });
    };

    if (!open) return null;

    return createPortal(
        <div className={styles.chooseOverlay} role="presentation" onClick={onClose}>
            <div
                className={styles.chooseModal}
                role="dialog"
                aria-modal="true"
                aria-label="Choose context"
                onClick={e => e.stopPropagation()}>
                <header className={styles.chooseHeader}>
                    <div>
                        <h2 className={styles.chooseTitle}>Choose context</h2>
                        <p className={styles.chooseSubtitle}>
                            Select the project information Lens should use when answering your
                            questions.
                        </p>
                    </div>
                    <button
                        type="button"
                        className={styles.chooseClose}
                        aria-label="Close"
                        onClick={onClose}>
                        <Dismiss16Regular />
                    </button>
                </header>

                <nav className={styles.chooseTabs} aria-label="Context type">
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            type="button"
                            className={`${styles.chooseTab} ${
                                tab === t.key ? styles.chooseTabActive : ''
                            }`}
                            onClick={() => setTab(t.key)}>
                            {t.label}
                        </button>
                    ))}
                </nav>

                <div className={styles.chooseBody}>
                    {tab === CONTEXT_MODE.ENTIRE_PROJECT ? (
                        <div className={styles.chooseEntire}>
                            <div className={styles.chooseEntireCard}>
                                <span className={styles.chooseRadio} aria-hidden>
                                    <span className={styles.chooseRadioDot} />
                                </span>
                                <div className={styles.chooseEntireCardText}>
                                    <p className={styles.chooseEntireCardTitle}>Entire Project</p>
                                    <p className={styles.chooseEntireCardDesc}>
                                        Use all ready VDR documents, IRL responses and observations
                                        across every workstream.
                                    </p>
                                </div>
                                <span className={styles.chooseEntireCheck} aria-hidden>
                                    <Checkmark12Filled color="#fff" />
                                </span>
                            </div>

                            <p className={styles.chooseSectionLabel}>Source readiness</p>
                            <div className={styles.chooseReadinessTable}>
                                {[
                                    { key: 'vdr', label: 'VDR', row: readiness.vdr },
                                    { key: 'irl', label: 'IRL', row: readiness.irl },
                                    {
                                        key: 'obs',
                                        label: 'Observations',
                                        row: readiness.obs,
                                    },
                                ].map(({ key, label, row }) => (
                                    <div key={key} className={styles.chooseReadinessRow}>
                                        <span className={styles.chooseReadinessType}>{label}</span>
                                        <div className={styles.chooseReadinessMid}>
                                            <span className={styles.chooseStat}>
                                                <ReadyDot />
                                                {row.ready} ready
                                            </span>
                                            {row.processing > 0 ? (
                                                <span
                                                    className={`${styles.chooseStat} ${styles.chooseStatProcessing}`}>
                                                    <ProcessingIcon />
                                                    {row.processing} processing
                                                </span>
                                            ) : null}
                                        </div>
                                        <span className={styles.chooseReadinessRight}>
                                            {row.ready} of {row.total} ready
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className={styles.chooseSummaryBar}>
                                <span className={styles.chooseStat}>
                                    <ReadyDot />
                                    <strong>{readiness.readyTotal} sources ready</strong>
                                </span>
                                {readiness.processingTotal > 0 ? (
                                    <span
                                        className={`${styles.chooseStat} ${styles.chooseStatProcessingDark}`}>
                                        <ProcessingIcon />
                                        {readiness.processingTotal} processing
                                    </span>
                                ) : null}
                            </div>

                            {readiness.processingTotal > 0 ? (
                                <div className={styles.chooseWarning}>
                                    <Warning16Regular className={styles.chooseWarningIcon} />
                                    <div>
                                        <p>
                                            {readiness.processingTotal} file
                                            {readiness.processingTotal === 1 ? '' : 's'} are still
                                            processing and will not be included yet. You can
                                            continue with the {readiness.readyTotal} ready sources.
                                        </p>
                                        <button
                                            type="button"
                                            className={styles.chooseWarningLink}
                                            onClick={() =>
                                                setShowProcessingDetails(v => !v)
                                            }>
                                            View processing files
                                            <ArrowDown12Regular
                                                className={
                                                    showProcessingDetails
                                                        ? styles.chooseChevronUp
                                                        : ''
                                                }
                                            />
                                        </button>
                                        {showProcessingDetails && processingDocs.length ? (
                                            <ul className={styles.chooseProcessingList}>
                                                {processingDocs.map(doc => (
                                                    <li key={doc.key}>{doc.title}</li>
                                                ))}
                                            </ul>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {tab === CONTEXT_MODE.SELECT_WORKSTREAMS ? (
                        <div className={styles.chooseWorkstreams}>
                            <p className={styles.choosePanelTitle}>Select workstreams</p>
                            <p className={styles.choosePanelDesc}>
                                Include all ready VDR documents, IRL responses and observations
                                within the selected workstreams.
                            </p>
                            <label className={styles.chooseSearch}>
                                <Search16Regular />
                                <input
                                    type="search"
                                    placeholder="Search workstreams"
                                    value={wsSearch}
                                    onChange={e => setWsSearch(e.target.value)}
                                />
                            </label>
                            <div className={styles.chooseWsList}>
                                {catalogLoading && !filteredWorkstreams.length ? (
                                    <p className={styles.chooseEmpty}>Loading workstreams…</p>
                                ) : filteredWorkstreams.length ? (
                                    filteredWorkstreams.map(row => {
                                        const checked = selectedWsSet.has(row.id);
                                        return (
                                            <label
                                                key={row.id}
                                                className={`${styles.chooseWsRow} ${
                                                    checked ? styles.chooseWsRowSelected : ''
                                                }`}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleWorkstream(row.id)}
                                                />
                                                <div className={styles.chooseWsMain}>
                                                    <span className={styles.chooseWsName}>
                                                        {row.name}
                                                    </span>
                                                    <span className={styles.chooseWsMeta}>
                                                        {row.vdr > 0 ? (
                                                            <span>VDR {row.vdr}</span>
                                                        ) : null}
                                                        {row.irl > 0 ? (
                                                            <span>IRL {row.irl}</span>
                                                        ) : null}
                                                        {row.obs > 0 ? (
                                                            <span>Observations {row.obs}</span>
                                                        ) : null}
                                                        {!row.vdr && !row.irl && !row.obs ? (
                                                            <span>No sources yet</span>
                                                        ) : null}
                                                    </span>
                                                </div>
                                                <div className={styles.chooseWsStats}>
                                                    <span className={styles.chooseStat}>
                                                        <ReadyDot />
                                                        {row.ready} ready
                                                    </span>
                                                    {row.processing > 0 ? (
                                                        <span
                                                            className={`${styles.chooseStat} ${styles.chooseStatProcessing}`}>
                                                            <ProcessingIcon />
                                                            {row.processing} processing
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </label>
                                        );
                                    })
                                ) : (
                                    <p className={styles.chooseEmpty}>No workstreams found.</p>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {tab === CONTEXT_MODE.CUSTOM ? (
                        <div className={styles.chooseCustom}>
                            <p className={styles.choosePanelTitle}>Select specific sources</p>
                            <p className={styles.choosePanelDesc}>
                                Choose individual sources across VDR, IRL and Observations.
                            </p>
                            <label className={styles.chooseSearch}>
                                <Search16Regular />
                                <input
                                    type="search"
                                    placeholder="Search sources"
                                    value={sourceSearch}
                                    onChange={e => setSourceSearch(e.target.value)}
                                />
                            </label>
                            <div className={styles.chooseFilters}>
                                <div className={styles.chooseFilterGroup}>
                                    {TYPE_FILTERS.map(f => (
                                        <button
                                            key={f.key}
                                            type="button"
                                            className={`${styles.choosePill} ${
                                                typeFilter === f.key ? styles.choosePillActive : ''
                                            }`}
                                            onClick={() => setTypeFilter(f.key)}>
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                                <span className={styles.chooseFilterDivider} />
                                <select
                                    className={styles.chooseWsSelect}
                                    value={wsFilter}
                                    onChange={e => setWsFilter(e.target.value)}
                                    aria-label="Filter by workstream">
                                    <option value="all">All workstreams</option>
                                    {originFilterOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                    {(workstreams || []).map(ws => (
                                        <option key={ws.id} value={String(ws.id)}>
                                            {ws.name || `Workstream ${ws.id}`}
                                        </option>
                                    ))}
                                </select>
                                <span className={styles.chooseFilterDivider} />
                                <div className={styles.chooseFilterGroup}>
                                    {STATUS_FILTERS.map(f => (
                                        <button
                                            key={f.key}
                                            type="button"
                                            className={`${styles.choosePill} ${
                                                statusFilter === f.key
                                                    ? styles.choosePillActive
                                                    : ''
                                            }`}
                                            onClick={() => setStatusFilter(f.key)}>
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.chooseResultsBar}>
                                <span>{filteredSources.length} results</span>
                                {readyFilteredSources.length > 0 ? (
                                    <button
                                        type="button"
                                        className={styles.chooseSelectAllLink}
                                        onClick={
                                            allReadyResultsSelected
                                                ? deselectAllReadyResults
                                                : selectAllReadyResults
                                        }>
                                        {allReadyResultsSelected
                                            ? 'Deselect all results'
                                            : 'Select all ready results'}
                                    </button>
                                ) : null}
                            </div>
                            <div className={styles.chooseSourceList}>
                                {catalogLoading && !filteredSources.length ? (
                                    <p className={styles.chooseEmpty}>Loading sources…</p>
                                ) : filteredSources.length ? (
                                    filteredSources.map(row => {
                                        const checked = selectedSourceKeys.has(row.key);
                                        return (
                                            <label
                                                key={row.key}
                                                className={`${styles.chooseSourceRow} ${
                                                    checked ? styles.chooseSourceRowSelected : ''
                                                }`}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleSource(row.key)}
                                                />
                                                <div className={styles.chooseSourceMain}>
                                                    <span className={styles.chooseSourceTitle}>
                                                        {row.title}
                                                    </span>
                                                    <span className={styles.chooseSourceMeta}>
                                                        <span className={styles.chooseTypeBadge}>
                                                            {row.type === 'vdr'
                                                                ? 'VDR'
                                                                : row.type === 'irl'
                                                                  ? 'IRL'
                                                                  : 'OBS'}
                                                        </span>
                                                        <span>{row.workstreamName}</span>
                                                    </span>
                                                </div>
                                                <div className={styles.chooseSourceRight}>
                                                    {row.sizeLabel ? (
                                                        <span>{row.sizeLabel}</span>
                                                    ) : null}
                                                    {row.dateLabel ? (
                                                        <span>{row.dateLabel}</span>
                                                    ) : null}
                                                    <span
                                                        className={
                                                            row.status === 'ready'
                                                                ? styles.chooseStatusReady
                                                                : styles.chooseStatusProcessing
                                                        }>
                                                        {row.status === 'ready'
                                                            ? 'Ready'
                                                            : 'Processing'}
                                                    </span>
                                                </div>
                                            </label>
                                        );
                                    })
                                ) : (
                                    <p className={styles.chooseEmpty}>No sources match this filter.</p>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>

                <footer className={styles.chooseFooter}>
                    <p className={styles.chooseFooterSummary}>{footerSummary}</p>
                    <div className={styles.chooseFooterActions}>
                        <button
                            type="button"
                            className={styles.chooseCancelBtn}
                            onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.chooseApplyBtn}
                            disabled={!canApply}
                            onClick={handleApply}>
                            Use context
                        </button>
                    </div>
                </footer>
            </div>
        </div>,
        document.body,
    );
};

export default AskAiChooseContextModal;
